import React, { useEffect, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { CreateNewInvestorDrawer } from "components/deal/Investors/Create-new-investor-drawer/CreateNewInvestorDrawer";
import { NumberInput } from "components/forms";
import { InvestorsGroupAutocomplete } from "components/Measure-data/Groups-form/InvestorsGroupAutocomplete";
import dayjs from "dayjs";
import { useActionHook } from "hooks";
import useDeals from "hooks/tagger-hooks/useDeals";
import { uniqBy } from "lodash";
import {
  useAccountSelector,
  useDateFormatSelect,
  useInvestorsSelector,
  useMeasureParametersSelector,
  useTaggerActions,
  useTaggerDocumentArraySelector,
  useTaggerTagByIdSelector
} from "store";
import { IApiServiceError } from "store/services/altius-api-error";
import { ApiValidationErrors, CurrencyType, IInvestor, TaggerSplitByGroup, TagTableRow } from "store/types";
import { IEditTagPayload, ITag } from "store/types/tagger/api";
import toIsoMidday from "utils/to-iso-midday";

import { currenciesArray } from "../../../constants";
import {
  useGetDealByIdQuery,
  useGetDealRoundInvestorsQuery
} from "../../../queries/query-selectors/tagger-query-selectors";
import { Dropdown } from "../../Tagger/General/Dropdown";

export type NewTag = Omit<
  ITag,
  | "id"
  | "value"
  | "account_id"
  | "effective_range_from"
  | "effective_range_to"
  | "split_id"
  | "is_analysis"
  | "is_draft"
  | "is_tagged"
  | "deleted_at"
  | "is_ai"
  | "is_splittable"
  | "draft_measure_data_id"
  | "measure_data_id"
  | "needs_sync"
  | "tag"
> & { value: number | null };

const defaultTag: NewTag = {
  document_id: null,
  deal: { id: 0, title: "" },
  measure: { id: 0, name: "" },
  investor: null,
  round: null,
  date: "",
  accounting_date: null,
  value: null,
  series_id: null,
  currency: "USD",
  description: null,
  children: null,
  is_child: false
};

type AddTagModalProps = {
  onCancel: () => void;
  onSubmit: (tagData: ITag | NewTag) => void;
  tag?: Pick<TagTableRow, "id" | "deal" | "is_child"> & { hasChildren: boolean };
};

export const AddTagModal: React.FC<AddTagModalProps> = ({ onCancel, onSubmit, tag: tagProps }) => {
  const { allow_split_by_group } = useAccountSelector() || {};
  const { editTag, addTag } = useTaggerActions();
  const allDocs = useTaggerDocumentArraySelector();
  const tagFromStore =
    useTaggerTagByIdSelector(tagProps?.id || 0, tagProps?.is_child || false, tagProps?.hasChildren ?? false) || null;
  const dateFormat = useDateFormatSelect();

  const { options: dealOptions, value } = useDeals();
  const relevantDeals = allDocs.map((doc) => doc.dealId);
  const filteredDealOptions = dealOptions.filter((option) => relevantDeals.includes(option.value));

  const [tagData, setTagData] = useState<ITag | NewTag>(
    tagFromStore
      ? { ...tagFromStore, value: tagFromStore.value ? Math.abs(tagFromStore.value) : null }
      : { ...defaultTag }
  );
  const selectedDeal = filteredDealOptions.find((o) => (o.value === tagData.deal.id ? tagData.deal.id : value)) || null;

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [createdInvestors, setCreatedInvestors] = useState<{ id: number; name: string }[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const investorsFromStore = useInvestorsSelector();

  const measures = useMeasureParametersSelector();
  const { data } = useGetDealRoundInvestorsQuery(selectedDeal?.value || null);
  const { data: dealData } = useGetDealByIdQuery(tagData.deal.id);

  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [splitTypeGroup, setSplitTypeGroup] = useState<TaggerSplitByGroup>("entire_commitment" as TaggerSplitByGroup);

  const roundInvestors = data || [];
  const isParent = !!tagData.children || false;
  const isParentTag = Boolean(
    tagFromStore && !tagFromStore.is_child && tagFromStore.children && tagFromStore.children.length > 0
  );
  const isChildWithChildren = Boolean(
    tagFromStore && tagFromStore.is_child && tagFromStore.children && tagFromStore.children.length > 0
  );

  const documentSeriesId = useMemo(() => {
    return allDocs.find((doc) => doc.dealId === tagData.deal.id)?.documentSeriesId || null;
  }, [tagData.deal.id, allDocs]);

  const resetTagData = (tag: ITag | null, deal?: { id: number; title: string }): NewTag => {
    return tag
      ? { ...tag, value: tag.value ? Math.abs(tag.value) : null }
      : { ...defaultTag, deal: deal || { id: 0, title: "" } };
  };

  useEffect(() => {
    setTagData(resetTagData(tagFromStore, tagProps?.deal));
    setValidationErrors({});
  }, [tagFromStore, tagProps?.deal]);

  useEffect(() => {
    // Only set currency from deal data when creating a new tag (not editing)
    // When editing, preserve the existing tag's currency
    if (dealData && !tagFromStore) {
      setTagData((prev) => ({
        ...prev,
        currency: dealData.currency?.data?.find((curr) => curr.is_default)?.value as CurrencyType
      }));
    }
  }, [dealData, tagFromStore]);

  type InvestorOption = { value: number; label: string };
  type SeparatorOption = { value: "__separator__"; label: string; isSeparator: true };
  type InvestorOptionType = InvestorOption | SeparatorOption;

  const investorsOptions = useMemo<InvestorOptionType[]>(() => {
    // Get deal investors from rounds
    const dealInvestorIds = uniqBy(roundInvestors?.map((round) => round.investors).flat(), "id") || [];
    const dealInvestors: InvestorOption[] = dealInvestorIds.map((inv) => ({
      value: inv.id,
      label: inv.name
    }));

    // Get account investors
    const accountInvestorsFromStore = investorsFromStore.map((inv) => ({
      value: inv.id,
      label: inv.name
    }));
    const accountInvestorsCreated = createdInvestors.map((inv) => ({
      value: inv.id,
      label: inv.name
    }));
    const accountInvestors = [...accountInvestorsFromStore, ...accountInvestorsCreated];

    const options: InvestorOptionType[] = [];

    if (dealInvestors.length > 0) {
      options.push({ value: "__separator__", label: "DEAL INVESTORS", isSeparator: true });
      options.push(...dealInvestors);
    }
    if (accountInvestors.length > 0) {
      options.push({ value: "__separator__", label: "ACCOUNT INVESTORS", isSeparator: true });
      options.push(...accountInvestors);
    }

    return options;
  }, [roundInvestors, investorsFromStore, createdInvestors]);

  const measureOptions = useMemo(() => {
    return measures.map((measure) => ({ value: measure.id, label: measure.name, global: measure.is_global_value }));
  }, [measures]);

  const roundOptions = useMemo(() => {
    return roundInvestors.map((round) => ({ value: round.round_id, label: round.round_name }));
  }, [roundInvestors]);

  const isMeasureGlobalValue = (measureId: number) => {
    return measureOptions.find((option) => option.value === measureId)?.global || false;
  };

  // Map API error keys to form field names
  const mapApiErrorsToFields = (apiErrors: ApiValidationErrors): Record<string, string> => {
    const mappedErrors: Record<string, string> = {};

    // Map API snake_case keys to form field names
    const errorMapping: Record<string, string> = {
      deal_id: "deal",
      measure_id: "measureId",
      investor_id: "investor",
      round_id: "round",
      value: "value",
      date: "date",
      accounting_date: "accountingDate",
      currency: "currency"
    };

    Object.keys(apiErrors).forEach((apiKey) => {
      const fieldKey = errorMapping[apiKey] || apiKey;
      if (apiErrors[apiKey] && apiErrors[apiKey].length > 0) {
        mappedErrors[fieldKey] = apiErrors[apiKey][0];
      }
    });

    return mappedErrors;
  };

  const { action: handleAddTag, loading: addTagLoading } = useActionHook(
    (data: { tag: NewTag; seriesId?: number }) => addTag(data),
    {
      onSuccess: () => {
        setValidationErrors({});
        onSubmit(tagData);
      },
      onReject: (err: IApiServiceError) => {
        // Extract errors from different possible error structures
        let apiErrors: ApiValidationErrors = {};

        // Check for errors property directly (ApiServiceError instance)
        if (err?.errors && typeof err.errors === "object") {
          apiErrors = err.errors;
        }
        // Check for nested response structure (AxiosError)
        else if (err?.response?.data?.errors) {
          apiErrors = err.response.data.errors;
        }

        const mappedErrors = mapApiErrorsToFields(apiErrors);
        setValidationErrors(mappedErrors);
      }
    }
  );

  const { action: handleEditTag, loading: editTagLoading } = useActionHook((data: IEditTagPayload) => editTag(data), {
    onSuccess: () => {
      setValidationErrors({});
      onSubmit(tagData);
    },
    onReject: (err: IApiServiceError) => {
      // Extract errors from different possible error structures
      let apiErrors: ApiValidationErrors = {};

      if (err instanceof Error && "errors" in err) {
        apiErrors = (err as IApiServiceError).errors || {};
      } else if (err?.errors) {
        apiErrors = err.errors;
      } else if (err?.response?.data?.errors) {
        apiErrors = err.response.data.errors;
      }

      const mappedErrors = mapApiErrorsToFields(apiErrors);
      setValidationErrors(mappedErrors);
    }
  });

  const saveData = async () => {
    // Client-side validation
    const clientErrors: Record<string, string> = {};

    if (!tagData.measure.id) {
      clientErrors.measure = "Parameter is required";
    }
    if (!tagData.value) {
      clientErrors.value = "Value is required";
    }
    if (!tagData.currency) {
      clientErrors.currency = "Currency is required";
    }
    if (!tagData.date) {
      clientErrors.date = "Date is required";
    }

    if (!isMeasureGlobalValue(tagData.measure.id) && !tagData.round && roundOptions.length > 1) {
      clientErrors.round = "Round is required";
    }

    if (!isMeasureGlobalValue(tagData.measure.id) && !isParent) {
      if (allow_split_by_group && Boolean(!tagData.investor?.id) && Boolean(!selectedGroups.length)) {
        clientErrors.investorsGroup = "Investors group is required";
      } else if (!allow_split_by_group && Boolean(!tagData.investor?.id)) {
        clientErrors.investor = "Investor is required";
      }
    }

    if (Object.keys(clientErrors).length > 0) {
      setValidationErrors(clientErrors);
      return;
    }

    setValidationErrors({});

    if (tagFromStore) {
      const editTagData: IEditTagPayload = {
        // ...tagData,
        id: tagFromStore.id,
        split_id: tagFromStore.split_id,
        children: tagFromStore.children,
        //send next fields to update only if they are changed from the original tag data
        is_draft: undefined, //we shouldn't send is_draft field to update from table, the only option to update it directly from 3 dots or tagmenu
        ...(tagData.value !== tagFromStore.value && { value: tagData.value || undefined }),
        ...(tagData.date !== tagFromStore.date && { date: tagData.date }),
        ...(tagData.accounting_date !== tagFromStore.accounting_date && { accounting_date: tagData.accounting_date }),
        ...(tagData.currency !== tagFromStore.currency && { currency: tagData.currency }),
        ...(tagData.description !== tagFromStore.description && { description: tagData.description }),
        ...(tagData.measure.id !== tagFromStore.measure.id && { measure_id: tagData.measure.id }),
        ...(tagData.investor?.id !== tagFromStore.investor?.id && { investor_id: tagData.investor?.id || null }), //we need to send null if global value measure was selected
        ...(tagData.round?.id !== tagFromStore.round?.id && { round_id: tagData.round?.id || null }), //we need to send null if global value measure was selected
        ...((tagData.measure.id !== tagFromStore.measure.id || tagData.date !== tagFromStore.date) && {
          is_child: tagData.is_child ?? false
        }),
        ...(documentSeriesId && { document_series_id: documentSeriesId })
      };
      await handleEditTag(editTagData);
    } else {
      await handleAddTag({
        tag: { ...tagData, deal: { id: selectedDeal?.value || 0, title: selectedDeal?.label || "" } },
        ...(documentSeriesId && { seriesId: documentSeriesId }),
        ...(selectedGroups.length && {
          investor_ids: investorsFromStore
            .filter((investor) => selectedGroups.includes(investor.group!))
            .map((investor) => investor.id)
        }),
        ...(selectedGroups.length && splitTypeGroup && { split_by: splitTypeGroup })
      });
    }
  };

  return (
    <Stack
      sx={{
        padding: "20px",
        width: "450px",
        overflow: "auto",
        gap: "20px"
      }}
    >
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center", pb: "8px", borderBottom: "1px solid #DBE8F5" }}
      >
        <Typography>Add Value</Typography>
        <IconButton onClick={onCancel}>
          <CloseIcon />
        </IconButton>
      </Stack>
      {/* <> */}
      <Stack gap="20px" flexDirection="column" justifyContent="flex-start" alignItems="stretch">
        <Dropdown
          label="Deal"
          disabled={!!tagProps?.deal}
          placeholder="Choose Deal"
          options={filteredDealOptions}
          value={selectedDeal}
          onChange={(e, option) => {
            if (!option) return;
            setTagData((prev) => ({ ...prev, deal: { id: option.value, title: option.label } }));
          }}
        />
        <Dropdown
          label="Parameter"
          placeholder="Choose Parameter"
          disabled={!selectedDeal}
          options={measureOptions}
          value={measureOptions.find((option) => option.value === tagData.measure.id) || null}
          onChange={(e, option) => {
            if (!option) return;
            setTagData((prev) => ({
              ...prev,
              measure: { id: option.value, name: option.label },
              ...(option &&
                isMeasureGlobalValue(option.value) && {
                  investor: null,
                  round: null,
                  investor_id: null,
                  round_id: null
                }),
              ...(option &&
                !isMeasureGlobalValue(option.value) &&
                roundOptions.length === 1 && {
                  round: { id: roundOptions[0].value, name: roundOptions[0].label }
                })
            }));
            setSelectedGroups([]);
            if (validationErrors.measure) {
              setValidationErrors({ ...validationErrors, measure: "" });
            }
          }}
          error={!!validationErrors.measure}
          helperText={validationErrors.measure}
        />
        <Stack direction="row" gap="20px" justifyContent="flex-start" alignItems="stretch">
          <NumberInput
            disabled={isParent || !selectedDeal}
            size="small"
            placeholder="Enter value"
            label="Value"
            fullWidth
            variant="outlined"
            value={tagData.value}
            onChange={(e) => {
              setTagData((prev) => ({ ...prev, value: e.target.value ? Number(e.target.value) : null }));
              if (validationErrors.value) {
                setValidationErrors({ ...validationErrors, value: "" });
              }
            }}
            error={!!validationErrors.value}
            helperText={validationErrors.value}
          />

          <Autocomplete
            size="small"
            multiple={false}
            fullWidth
            disableClearable
            disabled={!selectedDeal || !!tagProps?.is_child}
            value={tagData.currency || "USD"}
            options={currenciesArray}
            onChange={(e, option) => {
              setTagData((prev) => ({ ...prev, currency: option }));
              if (validationErrors.currency) {
                setValidationErrors({ ...validationErrors, currency: "" });
              }
            }}
            getOptionLabel={(option) => option}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                disabled={!selectedDeal || !!tagProps?.is_child}
                label={"Currency"}
                placeholder={"Choose Currency"}
                variant="outlined"
                error={!!validationErrors.currency}
                helperText={validationErrors.currency}
              />
            )}
          />
        </Stack>
        <Stack direction="row" gap="20px" justifyContent="flex-start" alignItems="stretch">
          <DesktopDatePicker
            disabled={!selectedDeal}
            format={dateFormat}
            views={["year", "month", "day"]}
            value={tagData.date ? dayjs(tagData.date) : null}
            slotProps={{
              inputAdornment: { position: "start" },
              popper: {
                sx: {
                  ".MuiPickersCalendarHeader-labelContainer": {
                    fontSize: "0.9rem"
                  }
                }
              },
              textField: {
                error: !!validationErrors.date,
                helperText: validationErrors.date
              }
            }}
            label="Date"
            onChange={(date) => {
              if (date && date.isValid()) {
                setTagData((prev) => ({
                  ...prev,
                  date: toIsoMidday(date)
                }));
                if (validationErrors.date) {
                  setValidationErrors({ ...validationErrors, date: "" });
                }
              }
            }}
          />
          <DesktopDatePicker
            disabled={!selectedDeal}
            format={dateFormat}
            views={["year", "month", "day"]}
            value={tagData.accounting_date ? dayjs(tagData.accounting_date) : null}
            slotProps={{
              inputAdornment: { position: "start" },
              popper: {
                sx: {
                  popper: {
                    sx: {
                      ".MuiPickersCalendarHeader-labelContainer": {
                        fontSize: "0.9rem"
                      }
                    }
                  },
                  ".MuiPickersCalendarHeader-labelContainer": {
                    fontSize: "0.9rem"
                  }
                }
              },
              textField: {
                error: !!validationErrors.accounting_date,
                helperText: validationErrors.accounting_date
              }
            }}
            label="Accounting Date"
            onChange={(date) => {
              if (date && date.isValid()) {
                setTagData((prev) => ({
                  ...prev,
                  accounting_date: toIsoMidday(date)
                }));
                if (validationErrors.accounting_date) {
                  setValidationErrors({ ...validationErrors, accounting_date: "" });
                }
              }
            }}
          />
        </Stack>
        {roundOptions.length > 1 && (
          <Dropdown
            label="Round"
            placeholder="Choose Round"
            sx={{
              "& .MuiAutocomplete-clearIndicator": {
                display: "none"
              }
            }}
            disabled={isMeasureGlobalValue(tagData.measure.id) || !selectedDeal}
            options={roundOptions}
            value={roundOptions.find((option) => option.value === tagData.round?.id) || null}
            onChange={(e, option) => {
              if (!option) return;
              setTagData((prev) => ({
                ...prev,
                round: { id: option.value, name: option.label },
                investor: null
              }));
              if (validationErrors.round) {
                setValidationErrors({ ...validationErrors, round: "" });
              }
            }}
            error={!!validationErrors.round}
            helperText={validationErrors.round}
          />
        )}
        {allow_split_by_group ? (
          <>
            <InvestorsGroupAutocomplete
              //can select single investor or investor groups
              //if selected investor, then groups will be empty
              //if selected groups, then investor will be null
              value={{
                investorId: tagData.investor?.id || null,
                groups: selectedGroups
              }}
              onChange={(value) => {
                const investor = investorsFromStore.find((investor) => investor.id === value.investorId);
                setTagData((prev) => ({
                  ...prev,
                  investor: investor ? { id: investor.id, name: investor.name, group: investor.group || null } : null
                }));
                if (validationErrors.investorsGroup) {
                  setValidationErrors({ ...validationErrors, investorsGroup: "" });
                }
                setSelectedGroups(value.groups);
              }}
              onCreateInvestor={() => setIsDrawerOpen(true)}
              disabled={
                !tagData.measure.id ||
                !tagData.round?.id ||
                isMeasureGlobalValue(tagData.measure.id) ||
                isParent ||
                !selectedDeal ||
                isChildWithChildren
              }
              error={!!validationErrors.investorsGroup}
              helperText={validationErrors.investorsGroup}
              dealInvestorIds={
                uniqBy(roundInvestors?.map((round) => round.investors).flat(), "id").map((investor) => investor.id) ||
                []
              }
              allInvestorsCommitmentOption={false}
            />
            {!isMeasureGlobalValue(tagData.measure.id) && allow_split_by_group && Boolean(selectedGroups.length) && (
              <>
                <RadioGroup
                  value={splitTypeGroup || ""}
                  onChange={(e) => setSplitTypeGroup(e.target.value as TaggerSplitByGroup)}
                >
                  <FormControlLabel
                    value="entire_commitment"
                    control={<Radio sx={{ "&.Mui-checked": { color: "#071F35" } }} />}
                    label="Split by entire commitment"
                  />
                  <FormControlLabel
                    value="group_commitment"
                    control={<Radio sx={{ "&.Mui-checked": { color: "#071F35" } }} />}
                    label="Split by group commitment"
                  />
                </RadioGroup>
              </>
            )}
          </>
        ) : (
          <Autocomplete
            disabled={
              !tagData.round?.id ||
              !tagData.measure.id ||
              isMeasureGlobalValue(tagData.measure.id) ||
              isParent ||
              !selectedDeal ||
              isChildWithChildren
            }
            options={investorsOptions || []}
            value={
              isParentTag
                ? ({ value: -1, label: "Multiple" } as InvestorOption)
                : investorsOptions?.find(
                    (option) => !("isSeparator" in option) && option.value === tagData.investor?.id
                  ) || null
            }
            onChange={(_, option) => {
              if (!option || "isSeparator" in option) return;
              setTagData((prev) => ({
                ...prev,
                investor: { id: option.value, name: option.label }
              }));
              if (validationErrors.investor) {
                setValidationErrors({ ...validationErrors, investor: "" });
              }
            }}
            PaperComponent={({ children }) => {
              return (
                <Paper
                  sx={{
                    position: "absolute",
                    width: "100%",
                    backgroundColor: "white",
                    zIndex: "10",
                    borderRadius: "10px",
                    "& .MuiAutocomplete-listbox": {
                      maxHeight: "28vh",
                      "& .MuiAutocomplete-option[aria-disabled='true']": {
                        opacity: 1,
                        "&.Mui-focused": {
                          opacity: 1
                        }
                      },
                      "& .MuiAutocomplete-option[aria-selected='true']": {
                        bgcolor: "#edf1f9",
                        "&.Mui-focused": {
                          bgcolor: "#edf1f9"
                        }
                      }
                    },
                    "& .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused": {
                      bgcolor: "#edf1f9"
                    }
                  }}
                >
                  <Paper>
                    {children}
                    <Button
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setIsDrawerOpen(true);
                      }}
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        height: "50px",
                        backgroundColor: "#fff",
                        boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                        borderRadius: "0 0 10px 10px"
                      }}
                      fullWidth
                    >
                      + Create New Investor
                    </Button>
                  </Paper>
                </Paper>
              );
            }}
            renderOption={(props, option) => {
              if ("isSeparator" in option) {
                return (
                  <Box
                    key={option.label}
                    display="flex"
                    alignItems="center"
                    padding="6px 20px"
                    sx={{ pointerEvents: "none" }}
                  >
                    <Typography fontSize="12px" fontWeight="bold" sx={{ color: "#496682" }}>
                      {option.label}
                    </Typography>
                  </Box>
                );
              }
              return (
                <li style={{ height: "43px", fontSize: "16px" }} {...props} key={option.value}>
                  {option.label}
                </li>
              );
            }}
            isOptionEqualToValue={(option, value) => {
              if ("isSeparator" in option || (value && "isSeparator" in value)) return false;
              if (!value || "isSeparator" in value) return false;
              return option.value === value.value;
            }}
            getOptionDisabled={(option) => "isSeparator" in option}
            renderInput={(params) => {
              return (
                <TextField
                  placeholder={"Choose Investor"}
                  sx={{ width: "100%" }}
                  {...params}
                  label="Investors"
                  error={!!validationErrors.investor}
                  helperText={validationErrors.investor}
                />
              );
            }}
          />
        )}

        <TextField
          size="small"
          disabled={!selectedDeal || isParent}
          label="Description"
          fullWidth
          variant="outlined"
          value={tagData.description}
          onChange={(e) => {
            setTagData((prev) => ({ ...prev, description: e.target.value }));
          }}
        />
      </Stack>
      <CreateNewInvestorDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        saveNewInvestorToSelected={(response: IInvestor) => {
          if (!response) return;
          setTagData((prev) => ({ ...prev, investor: { id: response.id, name: response.name } }));
          setCreatedInvestors([...createdInvestors, { id: response.id, name: response.name }]);
        }}
      />
      <Stack direction="row" sx={{ justifyContent: "flex-end", gap: "8px" }}>
        <Button variant="outlined" onClick={onCancel} disabled={addTagLoading || editTagLoading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={saveData} disabled={!selectedDeal || addTagLoading || editTagLoading}>
          Save
        </Button>
      </Stack>
    </Stack>
  );
};
